import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Visit, Client, Service, VisitParentWithVisits } from '../types';
import { visitParentAPI, clientAPI, serviceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import GroupVisitForm from './GroupVisitForm';
import AppDateField from './AppDateField';
import { getClientDisplayName } from '../utils/clientDisplay';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'scheduled':
      return 'primary';
    case 'cancelled':
      return 'error';
    case 'no-show':
      return 'warning';
    default:
      return 'default';
  }
};

// Helper function to format date as MM-DD-YYYY
// Parses date string directly to avoid timezone conversion issues
const formatVisitDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Not scheduled';
  try {
    // Parse the date string directly to avoid timezone conversion
    // Handle ISO format: "2024-01-15T00:00:00" or "2024-01-15T00:00:00Z" or "2024-01-15"
    const datePart = dateString.split('T')[0]; // Get YYYY-MM-DD part
    const [year, month, day] = datePart.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      // Fallback to Date parsing if direct parsing fails
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return format(date, 'MM-dd-yyyy');
    }
    
    return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
  } catch (error) {
    return 'Invalid date';
  }
};

// Helper function to format time as HH:MM AM/PM
const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }
    return format(date, 'h:mm a');
  } catch (error) {
    return 'Invalid time';
  }
};

// Helper function to get service code
const getServiceCode = (serviceId: number | undefined, services: Service[]): string => {
  if (!serviceId) return 'N/A';
  const service = services.find(s => s.service_id === serviceId);
  return service?.code || 'N/A';
};

const VisitsList: React.FC = () => {
  const { provider } = useAuth();
  const [visitOccurrences, setVisitOccurrences] = useState<VisitParentWithVisits[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    clientId: '',
    status: ''
  });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsData = await clientAPI.getClients(provider?.provider_id);
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    if (provider?.provider_id) {
      fetchClients();
    }
  }, [provider]);

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        setLoading(true);
        const filterParams = {
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          clientId: filters.clientId ? Number(filters.clientId) : undefined,
          status: filters.status || undefined
        };
        
        const visitsData = await visitParentAPI.list(provider?.provider_id as number, limit, skip, filterParams);
        const items = visitsData?.items || [];
        setVisitOccurrences(items);
        setTotal(visitsData?.total ?? items.length);
        const visitsArray: Visit[] = items.flatMap((o: VisitParentWithVisits) => o.visits || []);

        // Fetch services for all visits
        try {
          const visitServiceIds = visitsArray
            .map(v => v.service_id)
            .filter((id): id is number => id !== undefined && id !== null);
          
          if (visitServiceIds.length > 0) {
            const allServices = await serviceAPI.getServices();
            const servicesArray = Array.isArray(allServices) ? allServices : (allServices as any).items || [];
            const relevantServices = servicesArray.filter((s: Service) => 
              visitServiceIds.includes(s.service_id)
            );
            setServices(relevantServices);
          }
        } catch (error) {
          console.error('Error fetching services:', error);
          setServices([]);
        }
      } catch (error) {
        console.error('Error fetching visits:', error);
      } finally {
        setLoading(false);
      }
    };

    if (provider?.provider_id) {
      fetchVisits();
    }
  }, [provider, skip, limit, filters]);

  const handleFilterChange = (field: keyof typeof filters) => (event: any) => {
    const value = event.target.value;
    setFilters(prev => ({ ...prev, [field]: value }));
    setSkip(0); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      clientId: '',
      status: ''
    });
    setSkip(0);
  };

  const handleEdit = (visit: Visit) => {
    setEditingVisit(visit);
    setOpenForm(true);
  };

  const handleDeleteOccurrence = async (visitParentId: number) => {
    if (window.confirm('Delete this visit occurrence and all client line items for it?')) {
      try {
        await visitParentAPI.delete(visitParentId);
        const filterParams = {
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          clientId: filters.clientId ? Number(filters.clientId) : undefined,
          status: filters.status || undefined
        };
        const visitsData = await visitParentAPI.list(provider?.provider_id as number, limit, skip, filterParams);
        const items = visitsData?.items || [];
        setVisitOccurrences(items);
        setTotal(visitsData?.total ?? items.length);
      } catch (error) {
        console.error('Error deleting visit occurrence:', error);
      }
    }
  };

  const reloadVisits = async () => {
    if (!provider?.provider_id) return;
    const filterParams = {
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      clientId: filters.clientId ? Number(filters.clientId) : undefined,
      status: filters.status || undefined,
    };
    const visitsData = await visitParentAPI.list(provider.provider_id as number, limit, skip, filterParams);
    const items = visitsData?.items || [];
    setVisitOccurrences(items);
    setTotal(visitsData?.total ?? items.length);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2D4B73' }}>
          Visits
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingVisit(null);
            setOpenForm(true);
          }}
          sx={{
            backgroundColor: '#2D4B73',
            color: '#ffffff',
            border: '2px solid #1E3452',
            boxShadow: '0 4px 14px 0 rgba(45, 75, 115, 0.4)',
            '&:hover': {
              backgroundColor: '#1E3452',
              color: '#ffffff',
              boxShadow: '0 6px 20px 0 rgba(45, 75, 115, 0.5)',
            },
          }}
        >
          Create Visit
        </Button>
      </Box>

      {/* Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#2D4B73' }}>
            Filter Visits
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="Start Date"
                value={filters.startDate || null}
                onChange={v => setFilters(prev => ({ ...prev, startDate: v || '' }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="End Date"
                value={filters.endDate || null}
                onChange={v => setFilters(prev => ({ ...prev, endDate: v || '' }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Client</InputLabel>
                <Select
                  value={filters.clientId}
                  onChange={handleFilterChange('clientId')}
                  label="Client"
                >
                  <MenuItem value="">
                    <em>All Clients</em>
                  </MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.client_id} value={client.client_id}>
                      {`${client.first_name} ${client.last_name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                >
                  <MenuItem value="">
                    <em>All Statuses</em>
                  </MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={clearFilters}
                sx={{ mt: 1, backgroundColor: '#2D4B73', '&:hover': { backgroundColor: '#1E3452' } }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ 
        borderRadius: 0,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(45, 75, 115, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#2D4B73' }}>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Provider</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Ratio</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Time in</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Time out</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Service Units</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>UI (Y/N)</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitOccurrences.length > 0 ? (
                visitOccurrences.map((occ) => {
                  const visit = [...(occ.visits || [])].sort((a, b) => a.visit_id - b.visit_id)[0];
                  if (!visit) return null;
                  const ratioLabels = Array.from(
                    new Set((occ.visits || []).map(v => v.ratio).filter((r): r is string => !!r))
                  );
                  const clientName = (occ.visits || [])
                    .map(v => getClientDisplayName(clients, v.client_id))
                    .join(', ');
                  
                  return (
                    <TableRow key={occ.visit_parent_id} sx={{ 
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: 'rgba(45, 75, 115, 0.1)',
                      }
                    }}>
                      <TableCell sx={{ color: '#2D4B73' }}>
                        {provider ? `${provider.first_name || ''} ${provider.last_name || ''}`.trim() : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2D4B73' }}>
                        {clientName}
                      </TableCell>
                      <TableCell sx={{ color: '#2D4B73' }}>
                        {getServiceCode(visit.service_id, services)}
                      </TableCell>
                      <TableCell sx={{ color: '#2D4B73' }}>{ratioLabels.length ? ratioLabels.join(', ') : '—'}</TableCell>
                      <TableCell sx={{ color: '#2D4B73' }}>
                        {formatVisitDate(visit.scheduled_start)}
                      </TableCell>
                      <TableCell sx={{ color: '#2D4B73' }}>
                        {formatTime(visit.scheduled_start)}
                      </TableCell>
                      <TableCell sx={{ color: '#2D4B73' }}>
                        {formatTime(visit.scheduled_end)}
                      </TableCell>
                      <TableCell sx={{ color: '#2D4B73' }}>
                        {visit.service_units ?? 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#2D4B73' }}>
                        {visit.unusual_incident_flag ? 'Y' : 'N'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={visit.status || 'Unknown'}
                          color={getStatusColor(visit.status || '') as any}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(visit)}
                          sx={{ color: '#2D4B73' }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOccurrence(occ.visit_parent_id)}
                          sx={{ color: '#ef4444' }}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" sx={{ color: '#2D4B73', fontStyle: 'italic' }}>
                      No visits found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > limit && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Showing {skip + 1}-{Math.min(skip + limit, total)} of {total} visit occurrences
          </Typography>
          <Pagination
            count={Math.ceil(total / limit)}
            page={Math.floor(skip / limit) + 1}
            onChange={(_, newPage) => setSkip((newPage - 1) * limit)}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
            siblingCount={2}
            boundaryCount={1}
            variant="outlined"
            shape="rounded"
          />
        </Box>
      )}

      <GroupVisitForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditingVisit(null);
        }}
        onSaved={async () => {
          setOpenForm(false);
          setEditingVisit(null);
          setSuccess(editingVisit ? 'Visit updated successfully!' : 'Visit created successfully!');
          setTimeout(() => setSuccess(null), 5000);
          await reloadVisits();
        }}
        fixedProviderId={provider?.provider_id != null ? Number(provider.provider_id) : undefined}
        providers={[]}
        defaultProviderId={null}
        editingVisit={editingVisit}
      />
    </Box>
  );
};

export default VisitsList;
